import { ActionIcon, Textarea, useMantineColorScheme } from '@mantine/core';
import { Send, SquareX } from 'lucide-react';

interface ScoutChatInputProps {
  input: string;
  isLoading: boolean;
  canSend: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onCancel: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const ScoutChatInput = ({
  input,
  isLoading,
  canSend,
  textareaRef,
  placeholder = 'Ask a question...',
  onInputChange,
  onSend,
  onCancel,
  onKeyDown,
  className,
  style,
}: ScoutChatInputProps) => {
  const { colorScheme } = useMantineColorScheme();

  return (
    <div style={{ position: 'relative', ...style }} className={className}>
      <Textarea
        ref={textareaRef}
        placeholder={isLoading ? 'Scout is working...' : placeholder}
        autosize
        minRows={1}
        maxRows={6}
        value={input}
        onChange={(e) => onInputChange(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        styles={{
          input: {
            fontSize: '0.875rem',
            paddingRight: '2.5rem', // To make room for icon
            paddingTop: 'var(--mantine-spacing-sm)',
            paddingBottom: 'var(--mantine-spacing-sm)',
            borderRadius: '1.5rem',
            lineHeight: 1.5,
          },
        }}
      />
      {isLoading ? (
        <ActionIcon
          onClick={onCancel}
          variant='transparent'
          color='gray.7'
          aria-label='Stop Scout'
          style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <SquareX size={24} strokeWidth={1} />
        </ActionIcon>
      ) : (
        <ActionIcon
          onClick={canSend ? onSend : undefined}
          variant={canSend ? 'default' : 'transparent'}
          color={
            !canSend
              ? colorScheme === 'dark'
                ? 'gray.7'
                : 'gray.5'
              : undefined
          }
          aria-label={canSend ? 'Send message' : 'Type a message to send'}
          style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: canSend ? 'pointer' : 'default',
          }}
        >
          <Send size={20} strokeWidth={1.2} />
        </ActionIcon>
      )}
    </div>
  );
};
